<script setup lang="ts">
import MiiaFwSignin from '@/features/miiaFw/components/AuthSignin.vue'
import MiiaFwSignup from '@/features/miiaFw/components/AuthSignup.vue'
import MiiaFwConfig from '@/configs/config'
import { signout } from '@/features/miiaFw/utils/miiaFwCognito'
import { computed, useTemplateRef, watch } from 'vue'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useUserAttributes } from '@/features/miiaFw/composables/useUserAttributes'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/globalError'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import { useStoreUtils } from '@/composables/common/store/useStoreUtils'

const signin = useTemplateRef<InstanceType<typeof MiiaFwSignin>>('signin')
const signup = useTemplateRef<InstanceType<typeof MiiaFwSignup>>('signup')

const miiaFwStore = useMiiaFwStore()
const { isAuthorized } = storeToRefs(miiaFwStore)
const { getUserAttributesFromStore } = useUserAttributes()
const disasterStore = useDisasterStore()
const governmentSettingStore = useGovernmentSettingStore()
const router = useRouter()
const loadingView = useLoadingView()

/**
 * @description 未認証状態かどうかを返却する
 * @param { } - 引数なし
 * @return { boolean }  未認証の場合にtrue, 認証済みであればfalse
 */
const unauthorized = computed((): boolean => {
  return MiiaFwConfig.config.authorization.cognitoEnable && !isAuthorized.value
})
/**
 * @callback openSignin
 * @description サインイン用のモーダル画面を開く。
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignin = (): void => {
  signin.value?.openModal()
}
/**
 * @callback openSignup
 * @description サインアップ用のモーダル画面を開く。
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignup = (): void => {
  signup.value?.openModal()
}

/** 災害APIのquery叩く関数
 * @param govCode 自治体コード
 * @returns なし
 */
const getDisasters = async (govCode: string | undefined) => {
  if (!govCode) return
  await disasterStore.fetchAndSetDisasters({ govCode })
}

/** 自治体設定APIのquery叩く関数
 * @param govCode 自治体コード
 * @returns なし
 */
const getGovernmentSetting = async (govCode: string | undefined) => {
  if (!govCode) return
  await governmentSettingStore.fetchAndSetGovernmentSetting({ govCode })
}

/** onMountedで叩くAPIをまとめてハンドリングする
 * @param govCode 自治体コード
 * @returns なし
 */
const mountProcessing = async (govCode: string | undefined) => {
  await Promise.all([getDisasters(govCode), getGovernmentSetting(govCode)])
}

/**
 * ログイン画面の認証時の即時ウォッチャーで監視する
 * @param {boolean} newValue 未認証状態
 * @return {Promise<void>} 戻り値なし
 */
watch(unauthorized, async (newValue): Promise<void> => {
  if (newValue) {
    // 未認証のままであれば何もしない
    return
  }
  loadingView.setLoadingView(true)
  try {
    // ユーザ属性を取得
    const attribute = await getUserAttributesFromStore()

    // ログイン可能な権限ではない場合はサインアウトする
    const authority = attribute.attribute[MiiaFwConfig.config.sysId].authority
    if (authority === undefined || authority.length === 0) {
      await signout()
      throw new GlobalError([ERROR_CODES.NO_ACCESSIBLE_SCREENS])
    }

    // ユーザ属性から自治体コードを取得し、未取得または空文字の場合はサインアウトする
    const govCode = attribute.attribute?.['disaster-commonattr']?.govCode as string
    if (!govCode) {
      await signout()
      throw new GlobalError([ERROR_CODES.USER_INFO_FETCH_FAILED])
    }

    // ログイン時に必要なAPIを呼び出し、エラーの場合はサインアウト
    await mountProcessing(govCode).catch(async e => {
      await signout()
      throw e
    })

    // 自治体設定の機能オプションに`ScreeningAuth`または`EmergencyAuth`が含まれていなければサインアウトする
    if (
      !governmentSettingStore.includesFeatureOptionsScreening() &&
      !governmentSettingStore.includesFeatureOptionsEmergency()
    ) {
      await signout()
      throw new GlobalError([ERROR_CODES.NO_ACCESSIBLE_SCREENS])
    }

    // 自治体設定等により初期値が変わるようなstoreをリセット
    const storeUtils = useStoreUtils()
    storeUtils.resetForSignin()

    router.push({ path: ROUTER_PATHS.screening })
  } finally {
    loadingView.setLoadingView(false)
  }
})
</script>

<template>
  <div class="login">
    <MiiaFwSignin ref="signin"></MiiaFwSignin>
    <MiiaFwSignup ref="signup"></MiiaFwSignup>
    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 main-area">
      <div class="sysName">
        <span class="display-3">{{ MiiaFwConfig.config.sysName }}</span>
      </div>
      <div v-if="unauthorized" class="primary-area">
        <p class="text-area">サインインしてご利用ください。</p>
        <button @click="openSignin" class="btn btn-primary ff-btn-primary">
          <a>&nbsp;サインイン&nbsp;</a>
        </button>
      </div>
      <hr class="my-4" />
      <div
        class="secondary-area"
        v-if="MiiaFwConfig.config.authorization.userSignupEnable && unauthorized"
      >
        <p class="text-area">ユーザーIDをお持ちでない方は以下よりユーザー登録をお願いします。</p>
        <button @click="openSignup" class="btn btn-secondary ff-btn-secondary">
          <a>ユーザー登録</a>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sysName {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 10vh;
  font-size: 5vw;
}

.main-area {
  padding-top: 50px;
  padding-bottom: 20px;
  margin-top: 50px;
  border: solid 1px #dcdcdc;
  text-align: center;
  width: 85%;
  margin-left: auto;
  margin-right: auto;
}

.primary-area {
  margin-right: 30px;
  margin-left: 30px;
  margin-top: 50px;
  margin-bottom: 60px;
}

.secondary-area {
  margin: 30px;
}

.text-area {
  font-size: 1.1em;
}

.btn-primary {
  background-color: #01916d;
  border: solid 1px #01916d;
  padding-left: 160px;
  padding-right: 160px;
  padding-top: 20px;
  padding-bottom: 20px;
  border-radius: 3px;
  font-size: 20px;
}

.btn-secondary {
  background-color: #ffffff;
  color: #333333;
  border: solid 1px #333333;
  padding-left: 160px;
  padding-right: 160px;
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 20px;
}
</style>
